import { useEffect, useState, useMemo } from "react";
import { getallfiles, deletemediafile } from "../..";
import { falsyToemptyString } from "../../../Admin/helper";
import { isAuthenticated } from "../../../Auth/helper";

const ITEMS_PER_PAGE = 15;

// Category accent colors — matches AdminDashboard palette
const CAT_STYLE = {
  Videos: { bg: "#eaf3de", color: "#3b6d11", icon: "fa-video" },
  Speech: { bg: "#e1f5ee", color: "#0f6e56", icon: "fa-microphone" },
  Songs: { bg: "#eaf3de", color: "#3b6d11", icon: "fa-music" },
  Images: { bg: "#faeeda", color: "#854f0b", icon: "fa-image" },
  Documents: { bg: "#fcebeb", color: "#a32d2d", icon: "fa-file-alt" },
  Audios: { bg: "#e6f1fb", color: "#185fa5", icon: "fa-headphones" },
};

const getCatStyle = (name) =>
  CAT_STYLE[name] || { bg: "#f5f5f5", color: "#555", icon: "fa-file" };

const DeleteMedia = () => {
  const [allprdt, setprdt] = useState([]);
  const [updateMedia, setMedia] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const curent = isAuthenticated();

    const fetchAllProduct = async () => {
      if (!curent?.user?._id || !curent?.token) {
        alert("Please login first");
        return;
      }

      await getallfiles(curent.user._id, curent.token)
        .then((r) => setprdt(r))
        .catch(() => alert("Error in processing request"));
    };

    fetchAllProduct();
  }, [updateMedia]);

  const deletemedia = async (id) => {
    if (!window.confirm("Are you sure you want to delete this media?")) return;

    const cu = isAuthenticated();

    if (!cu?.user?._id || !cu?.token) {
      alert("Please login first");
      return;
    }

    await deletemediafile(cu.user._id, cu.token, id)
      .then((re) => {
        if (re.message) alert("Product deleted successfully");
        setMedia(!updateMedia);
      })
      .catch(() => alert("Error in deleting media."));
  };


  // Unique category list for filter tabs
  const categories = useMemo(() => {
    const cats = [
      ...new Set(
        allprdt.map((p) => p?.productCategory?.categoryName).filter(Boolean),
      ),
    ];
    return ["All", ...cats.sort()];
  }, [allprdt]);

  // Filter + search
  const filtered = useMemo(() => {
    let result = [...allprdt];
    if (filterCat !== "All") {
      result = result.filter(
        (p) => p?.productCategory?.categoryName === filterCat,
      );
    }
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      result = result.filter(
        (p) =>
          p.productName?.toLowerCase().includes(q) ||
          p?.productCategory?.categoryName?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [allprdt, filterCat, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const goToPage = (p) => setCurrentPage(p);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCat]);

  // Pagination bar
  const PaginationBar = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      )
        pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    const btnBase = {
      border: "0.5px solid #e5e7eb",
      borderRadius: 6,
      padding: "5px 11px",
      fontSize: 12,
      cursor: "pointer",
      fontWeight: 500,
      minWidth: 34,
      background: "#fff",
      color: "#333",
    };
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 14,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: "#888" }}>
          Showing{" "}
          {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, filtered.length)}–
          {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{" "}
          {filtered.length} items
        </span>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              ...btnBase,
              opacity: currentPage === 1 ? 0.4 : 1,
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            ‹ Prev
          </button>
          {pages.map((p, i) =>
            p === "..." ? (
              <span
                key={`e${i}`}
                style={{ padding: "5px 4px", color: "#aaa", fontSize: 12 }}
              >
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => goToPage(p)}
                style={{
                  ...btnBase,
                  background: currentPage === p ? "#E34607" : "#fff",
                  color: currentPage === p ? "#fff" : "#333",
                  border:
                    currentPage === p
                      ? "0.5px solid #E34607"
                      : "0.5px solid #e5e7eb",
                }}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              ...btnBase,
              opacity: currentPage === totalPages ? 0.4 : 1,
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        border: "0.5px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "0.5px solid #e5e7eb",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: "#fcebeb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fas fa-trash-alt"
              style={{ fontSize: 12, color: "#a32d2d" }}
            ></i>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>
            Delete / update media
          </span>
        </div>
        <span
          style={{
            backgroundColor: "#e6f1fb",
            color: "#185fa5",
            fontSize: 12,
            fontWeight: 500,
            padding: "3px 12px",
            borderRadius: 20,
          }}
        >
          {allprdt.length} total items
        </span>
      </div>

      <div style={{ padding: "14px 16px" }}>
        {/* Search + filter row */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <i
              className="fas fa-search"
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#bbb",
                fontSize: 13,
              }}
            />
            <input
              type="text"
              placeholder="Search by name or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px 8px 32px",
                border: "0.5px solid #e0e0e0",
                borderRadius: 8,
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
                background: "#fafafa",
              }}
            />
            {searchQuery && (
              <i
                className="fas fa-times"
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#bbb",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              />
            )}
          </div>

          {/* Category filter tabs */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {categories.map((cat) => {
              const isActive = filterCat === cat;
              const cs =
                cat !== "All"
                  ? getCatStyle(cat)
                  : { bg: "#1a1a1a", color: "#fff" };
              return (
                <button
                  key={cat}
                  onClick={() => setFilterCat(cat)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 20,
                    border: "none",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? cat === "All"
                        ? "#1a1a1a"
                        : cs.bg
                      : "#f0f0f0",
                    color: isActive
                      ? cat === "All"
                        ? "#fff"
                        : cs.color
                      : "#666",
                    transition: "all 0.15s",
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* No results */}
        {filtered.length === 0 && (
          <div
            style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}
          >
            <i
              className="fas fa-inbox fa-2x"
              style={{ marginBottom: 10, opacity: 0.3 }}
            ></i>
            <p style={{ fontSize: 14 }}>
              {searchQuery || filterCat !== "All"
                ? "No items match your search / filter"
                : "No media files found"}
            </p>
          </div>
        )}

        {/* Table */}
        {filtered.length > 0 && (
          <>
            <div
              style={{
                border: "0.5px solid #e5e7eb",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {/* Table head */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 0.7fr",
                  backgroundColor: "#f5f5f5",
                  padding: "10px 14px",
                  borderBottom: "0.5px solid #e5e7eb",
                }}
              >
                {["Media name", "Type", "Action"].map((h) => (
                  <span
                    key={h}
                    style={{ fontSize: 12, fontWeight: 600, color: "#555" }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* Rows */}
              {paginated.map((item, idx) => {
                const catName = falsyToemptyString(
                  item?.productCategory?.categoryName,
                );
                const cs = getCatStyle(catName);
                return (
                  <div
                    key={item._id || idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.2fr 0.7fr",
                      padding: "10px 14px",
                      borderBottom:
                        idx < paginated.length - 1
                          ? "0.5px solid #f0f0f0"
                          : "none",
                      backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                      alignItems: "center",
                    }}
                  >
                    {/* Name */}
                    <span
                      style={{
                        fontSize: 13,
                        color: "#222",
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      <i
                        className={`fas ${cs.icon}`}
                        style={{ color: cs.color, fontSize: 12, flexShrink: 0 }}
                      ></i>
                      {item.productName}
                    </span>

                    {/* Category badge */}
                    <span>
                      <span
                        style={{
                          backgroundColor: cs.bg,
                          color: cs.color,
                          fontSize: 11,
                          fontWeight: 500,
                          padding: "2px 9px",
                          borderRadius: 20,
                        }}
                      >
                        {catName || "—"}
                      </span>
                    </span>

                    {/* Delete button */}
                    <span>
                      <button
                        onClick={() => deletemedia(item._id)}
                        title="Delete media"
                        style={{
                          backgroundColor: "#fcebeb",
                          color: "#a32d2d",
                          border: "none",
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <i
                          className="fas fa-trash"
                          style={{ fontSize: 11 }}
                        ></i>
                        Delete
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <PaginationBar />
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteMedia;
